if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[name="add"]') || this.querySelector('[type="submit"]');
        this.buyNowButton = this.querySelector('[data-tiara-buy-now]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.onPageShow = this.onPageShow.bind(this);
        // Only expose checkout once the gift-preserving AJAX submit handler exists.
        this.buyNowButton?.removeAttribute('hidden');
      }

      connectedCallback() {
        window.addEventListener('pageshow', this.onPageShow);
      }

      disconnectedCallback() {
        window.removeEventListener('pageshow', this.onPageShow);
      }

      onPageShow(event) {
        if (!event.persisted || (!this.redirecting && !this.submitting)) return;
        // Checkout may return this exact page from the browser's back/forward cache.
        // Clear transient request locks, leaving native variant availability intact.
        this.redirecting = false;
        this.submitting = false;
        this.error = false;
        [this.submitButton, this.buyNowButton].filter(Boolean).forEach((button) => {
          button.removeAttribute('aria-disabled');
          button.removeAttribute('aria-busy');
          button.classList.remove('loading');
          button.querySelector('.loading__spinner')?.classList.add('hidden');
        });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitting || this.redirecting || this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') return;
        const buyNow = Boolean(this.buyNowButton && evt.submitter === this.buyNowButton);
        if (buyNow && this.buyNowButton.disabled) return;
        const activeButton = buyNow ? this.buyNowButton : this.submitButton;
        const activeSpinner = activeButton.querySelector('.loading__spinner');
        this.submitting = true;
        this.error = false;

        this.handleErrorMessage();

        [this.submitButton, this.buyNowButton].filter(Boolean).forEach((button) => {
          button.setAttribute('aria-disabled', 'true');
          button.setAttribute('aria-busy', 'true');
        });
        activeButton.classList.add('loading');
        activeSpinner?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        window.TiaraBogo?.prepareFormData?.(formData);
        if (this.cart && !buyNow) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then(async (response) => {
            const state = await response.json();
            if (!response.ok && !state.status) state.status = response.status;
            return state;
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description || response.message || window.cartStrings?.error);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (buyNow) {
              // The same /cart/add request already added paid items and gifts.
              // Keep Shopify's existing event path; checkout reads this cart.
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              this.redirecting = true;
              const root = window.Shopify?.routes?.root || '/';
              window.location.assign(`${root}checkout`);
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error) {
              /*
               * A secondary cart subscriber must never block the primary
               * customer action. Keep drawer rendering independent so the
               * added item appears immediately even if an optional quantity
               * rule or app subscriber fails.
               */
              try {
                Promise.resolve(
                  publish(PUB_SUB_EVENTS.cartUpdate, {
                    source: 'product-form',
                    productVariantId: variantId,
                    cartData: response,
                  })
                )
                  .then(() => CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker))
                  .catch((eventError) => console.error('[Tiara cart subscriber]', eventError));
              } catch (eventError) {
                console.error('[Tiara cart subscriber]', eventError);
              }
            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            const isArabic = document.documentElement.lang?.toLowerCase().startsWith('ar');
            this.handleErrorMessage(isArabic ? 'تعذر إكمال الإضافة. راجعي اتصالك وحاولي مرة تانية.' : 'Unable to add this item. Check your connection and try again.');
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            activeButton.classList.remove('loading');
            activeSpinner?.classList.add('hidden');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.redirecting) {
              this.submitting = false;
              [this.submitButton, this.buyNowButton].filter(Boolean).forEach((button) => {
                button.removeAttribute('aria-busy');
                if (!this.error) button.removeAttribute('aria-disabled');
              });
            }

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (this.buyNowButton) {
          this.buyNowButton.toggleAttribute('disabled', disable);
          const label = this.buyNowButton.querySelector('span');
          if (label) label.textContent = disable && text ? text : this.buyNowButton.dataset.label;
        }
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = this.submitButton.dataset.label || window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
